import axiosInstance from "./axiosInstance";
import { getApiErrorMessage } from "../utils/apiErrorMessage";

export interface LeaveEntitlementApi {
  publicId: string;
  leavePolicyPublicId: string;
  leavePolicyName: string;
  leaveTypePublicId: string;
  leaveTypeName: string;
  annualQuota: number;
  maxBalance: number;
  carryForwardLimit: number;
  monthlyAccrual: number;
  isActive: boolean;
}

export type UpdateLeavePolicyDetailPayload = Omit<
  CreateLeavePolicyDetailPayload,
  "leavePolicyPublicId" | "leaveTypePublicId"
>;

export interface CreateLeavePolicyDetailPayload {
  leavePolicyPublicId: string;
  leaveTypePublicId: string;
  annualQuota: number;
  maxBalance: number;
  carryForwardLimit: number;
  monthlyAccrual: number;
  isActive: boolean;
}

export interface LeavePolicyDetails {
  publicId: string;
  policyName: string;
  employeeTypePublicId: string | null;
  employeeTypeName: string | null;
  employmentStatusPublicId: string | null;
  employmentStatusName: string | null;
  gradePublicId: string | null;
  gradeName: string | null;
  isActive: boolean;
}


export interface LeavePolicyListItem {
  publicId: string;
  policyName: string;
  employeeTypeName: string | null;
  employmentStatusName: string | null;
  gradeName: string | null;
  isActive: boolean;
}

export interface LeavePolicyListPage {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  success?: boolean;
  message?: string;
  data: LeavePolicyListItem[];
}

export interface SaveLeavePolicyPayload {
  policyName: string;
  employeeTypePublicId: string;
  employmentStatusPublicId: string;
  gradePublicId: string;
  isActive: boolean;
}

export interface SaveLeavePolicyResponse {
  success?: boolean;
  message?: string;
  data?: unknown;
}

export interface EmployeeTypeOption {
  publicId: string;
  typeName: string;
  isActive: boolean;
}

export interface GradeOption {
  publicId: string;
  gradeName: string;
  isActive: boolean;
}

export interface EmploymentStatusOption {
  publicId: string;
  statusName: string;
  displayOrder: number;
  isActive: boolean;
}

export interface LeaveTypeOption {
  publicId: string;
  leaveName: string;
  isActive: boolean;
}


export const getAuthHeader = () => {
  const token = localStorage.getItem("fhims_auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};


// GET EMPLOYEE TYPES FROM THE API FOR DROPDOWN
export const getEmployeeTypes = async (
  isActive = true,
): Promise<EmployeeTypeOption[]> => {
  const response = await axiosInstance.get<{
    success?: boolean;
    message?: string;
    data: EmployeeTypeOption[];
  }>("/hr/settings/employee-types", {
    headers: getAuthHeader(),
    params: { isActive },
  });
  if (response.data.success === false || !Array.isArray(response.data.data)) {
    throw new Error(getApiErrorMessage(response.data, "Failed to load employee types."));
  }
  return response.data.data;
};

// GET EMPLOYMENT STATUSES FROM THE API FOR DROPDOWN
export const getEmploymentStatuses = async (
  isActive = true,
): Promise<EmploymentStatusOption[]> => {
  const response = await axiosInstance.get<{
    success?: boolean;
    message?: string;
    data: EmploymentStatusOption[];
  }>("/hr/settings/employment-statuses", {
    headers: getAuthHeader(),
    params: { isActive },
  });
  if (response.data.success === false || !Array.isArray(response.data.data)) {
    throw new Error(
      getApiErrorMessage(response.data, "Failed to load employment statuses."),
    );
  }
  return response.data.data;
};

// GET GRADES FROM THE API FOR DROPDOWN
export const getGrades = async (isActive = true): Promise<GradeOption[]> => {
  const response = await axiosInstance.get<{
    success?: boolean;
    message?: string;
    data: GradeOption[];
  }>("/hr/settings/grades", {
    headers: getAuthHeader(),
    params: { isActive },
  });
  if (response.data.success === false || !Array.isArray(response.data.data)) {
    throw new Error(getApiErrorMessage(response.data, "Failed to load grades."));
  }
  return response.data.data;
};

// GET LEAVE TYPES FROM THE API FOR DROPDOWN
export const getLeaveTypes = async (
  isActive = true,
): Promise<LeaveTypeOption[]> => {
  const response = await axiosInstance.get<{
    success?: boolean;
    message?: string;
    data: LeaveTypeOption[];
  }>("/hr/settings/leave-types", {
    headers: getAuthHeader(),
    params: { isActive },
  });
  if (response.data.success === false || !Array.isArray(response.data.data)) {
    throw new Error(getApiErrorMessage(response.data, "Failed to load leave types."));
  }
  return response.data.data;
};

// GET LEAVE POLICIES FROM THE API FOR LISTING
export const getLeavePolicies = async (params: {
  PolicyName?: string;
  PageNumber: number;
  PageSize: number;
}): Promise<LeavePolicyListPage> => {
  const response = await axiosInstance.get<{
    success?: boolean;
    message?: string;
    data: LeavePolicyListPage;
  }>("/hr/settings/leave-policies/search", {
    headers: getAuthHeader(),
    params,
  });
  const result = response.data;
  if (
    result.success === false ||
    result.data?.success === false ||
    !Array.isArray(result.data?.data)
  ) {
    throw new Error(
      result.data?.message ||
        result.message ||
        "Failed to load leave policies.",
    );
  }
  return result.data;
};

// CREATE A NEW LEAVE POLICY
export const createLeavePolicy = async (
  payload: SaveLeavePolicyPayload,
): Promise<SaveLeavePolicyResponse> => {
  const response = await axiosInstance.post<SaveLeavePolicyResponse>(
    "/hr/settings/leave-policies",
    payload,
    { headers: getAuthHeader() },
  );
  if (response.data?.success === false) {
    throw new Error(getApiErrorMessage(response.data, "Failed to create leave policy."));
  }
  return response.data;
};

// UPDATE AN EXISTING LEAVE POLICY
export const updateLeavePolicy = async (
  publicId: string,
  payload: SaveLeavePolicyPayload,
): Promise<SaveLeavePolicyResponse> => {
  const response = await axiosInstance.put<SaveLeavePolicyResponse>(
    `/hr/settings/leave-policies/${encodeURIComponent(publicId)}`,
    payload,
    { headers: getAuthHeader() },
  );
  if (response.data?.success === false) {
    throw new Error(getApiErrorMessage(response.data, "Failed to update leave policy."));
  }
  return response.data;
};

// DELETE A LEAVE POLICY BY PUBLICID
export const deleteLeavePolicy = async (publicId: string): Promise<void> => {
  const response = await axiosInstance.delete<{
    success?: boolean;
    message?: string;
  }>(`/hr/settings/leave-policies/${encodeURIComponent(publicId)}`, {
    headers: getAuthHeader(),
  });
  if (response.data?.success === false) {
    throw new Error(getApiErrorMessage(response.data, "Failed to delete leave policy."));
  }
};


// ADD LEAVE DETAISL API INTEGRATION START
// GET LEAVE POLICY DETAILS BY PUBLICID
export const getLeavePolicyDetails = async (
  publicId: string,
): Promise<LeavePolicyDetails> => {
  const response = await axiosInstance.get<{
    success?: boolean;
    message?: string;
    data: LeavePolicyDetails;
  }>(`/hr/settings/leave-policies/${encodeURIComponent(publicId)}`, {
    headers: getAuthHeader(),
  });
  if (response.data.success === false || !response.data.data?.publicId) {
    throw new Error(getApiErrorMessage(response.data, "Failed to load leave policy."));
  }
  return response.data.data;
};

// GET LEAVE ENTITLEMENTS BY POLICY PUBLICID LISTING API
export const getLeaveEntitlements = async (policyPublicId: string): Promise<LeaveEntitlementApi[]> => {
  const response = await axiosInstance.get<{
    success?: boolean;
    message?: string;
    data: LeaveEntitlementApi[];
  }>(`/hr/settings/leave-policy-details/policy/${encodeURIComponent(policyPublicId)}`, {
    headers: getAuthHeader(),
  });
  if (response.data.success === false || !Array.isArray(response.data.data)) {
    throw new Error(getApiErrorMessage(response.data, "Failed to load leave entitlements."));
  }
  return response.data.data;
};

// ADD LEAVE POLICY DETAIL FUNCTION START
export const createLeavePolicyDetail = async (
  payload: CreateLeavePolicyDetailPayload,
): Promise<SaveLeavePolicyResponse> => {
  const response = await axiosInstance.post<SaveLeavePolicyResponse>(
    "/hr/settings/leave-policy-details",
    payload,
    { headers: getAuthHeader() },
  );
  if (response.data?.success === false) {
    throw new Error(
      getApiErrorMessage(response.data, "Failed to add leave entitlement."),
    );
  }
  return response.data;
};


// UPDATE LEAVE POLICY DETAIL FUNCTION START
export const updateLeavePolicyDetail = async (
  publicId: string,
  payload: UpdateLeavePolicyDetailPayload,
): Promise<SaveLeavePolicyResponse> => {
  const response = await axiosInstance.put<SaveLeavePolicyResponse>(
    `/hr/settings/leave-policy-details/${encodeURIComponent(publicId)}`,
    payload,
    { headers: getAuthHeader() },
  );
  if (response.data?.success === false) {
    throw new Error(
      getApiErrorMessage(response.data, "Failed to update leave entitlement."),
    );
  }
  return response.data;
};


// DELETE LEAVE POLICY DETAIL FUNCTION START
export const deleteLeavePolicyDetail = async (
  publicId: string,
): Promise<void> => {
  const response = await axiosInstance.delete<SaveLeavePolicyResponse>(
    `/hr/settings/leave-policy-details/${encodeURIComponent(publicId)}`,
    { headers: getAuthHeader() },
  );
  if (response.data?.success === false) {
    throw new Error(
      getApiErrorMessage(response.data, "Failed to delete leave entitlement."),
    );
  }
};
